<template>
    <div>
        <div class="mt-5 flex flex-col md:flex-row justify-arrow">
            <div class="flex flex-col items-center md:items-start w-full">
                <h1 class="text-3xl text-center md:text-left">
                    {{ slug ? "" : "Nueva página personalizada" }}
                    <strong v-if="slug" class="text-theme-primary">
                        {{ title }}
                    </strong>
                </h1>
                <div v-if="slug" class="flex">
                    <strong class="text-sm text-theme-muted">
                        Slug {{ itemComponent.slug }}
                    </strong>
                </div>
            </div>
            <div
                v-if="slug"
                class="flex flex-col mt-2 md:mt-0 md:min-w-max justify-center text-center"
            >
                <small> 📆💾 {{ itemComponent.createdAt | date }} </small>
                <small> 📆✏ {{ itemComponent.updatedAt | date }} </small>
            </div>
        </div>

        <div class="my-4 flex flex-wrap">
            <custom-navbar
                id="tabs-order"
                v-model="activeTab"
                center-collapsed
                not-line
            >
                <template #left>
                    <custom-navbar-item id="home">
                        <router-link
                            to="/app/blogs"
                            class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                        >
                            <span>🏠</span>
                        </router-link>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="general"
                        :active="['general', 'home'].includes(activeTab)"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden"> General&nbsp; </span>
                            <span>⚙️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug"
                        id="products"
                        :active="activeTab === 'products'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Productos&nbsp;
                            </span>
                            <span>🛍️</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        v-if="slug"
                        id="files"
                        :active="activeTab === 'files'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Archivos adjuntos&nbsp;
                            </span>
                            <span>🗂</span>
                        </div>
                    </custom-navbar-item>
                    <custom-navbar-item
                        id="advance"
                        :active="activeTab === 'advance'"
                        class="px-2 md:px-4 xl:px-6"
                    >
                        <div class="flex">
                            <span class="lg:block hidden">
                                Avanzado&nbsp;
                            </span>
                            <span>🤖</span>
                        </div>
                    </custom-navbar-item>
                </template>
                <template #right>
                    <div class="hidden md:block">
                        <custom-switch
                            v-if="$ability.hasPermission('blog:Update') && slug"
                            v-model="enabledUpdate"
                        >
                            <span class="text-theme-inverse">🖊</span>
                        </custom-switch>
                    </div>
                </template>
            </custom-navbar>
        </div>

        <div
            v-if="$ability.hasPermission('blog:Update') && slug"
            class="flex justify-center"
        >
            <div class="block md:hidden my-3">
                <custom-switch v-model="enabledUpdate">
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
            </div>
        </div>

        <div v-show="['general', 'home'].includes(activeTab)" id="blog-general">
            <div id="general-title" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h3 class="text-lg">Características Generales</h3>
                    <span class="mt-3">
                        Las páginas personalizadas son una parte fundamental de
                        la estrategia de contenido de cualquier ecommerce. Con
                        las características generales de la página tienes la
                        oportunidad de generar contenido útil, incluir palabras
                        clave relevantes y atraer tráfico orgánico.
                    </span>
                </div>
            </div>

            <div v-if="slug" class="flex flex-wrap">
                <div
                    v-if="$ability.hasPermission('blog:Publish')"
                    class="w-full mt-8 flex justify-center items-center"
                >
                    <div>
                        <active-selector
                            v-model="itemComponent.active"
                            :enabled="enabledUpdate"
                            :weight="parseInt(itemComponent.weight)"
                            :public-url="publicBlogUrl"
                            :public-label="publicBlogLabel"
                            require-weight
                            on-label="Publicada"
                            off-label="Oculta"
                            activate-title="¿Quieres publicar la página?"
                            activate-hint="Recuerda cargar la imagen de la página. Selecciona la posición y el peso con los que quieres que aparezca en el listado."
                            confirm-label="Activar página"
                            @change="publishComponent"
                            @weight-change="itemComponent.weight = $event"
                            @page-change="
                                itemComponent.weight = ($event - 1) * 12 + 1
                            "
                        />
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap">
                <div class="w-full mt-2">
                    <custom-input
                        v-model="itemComponent.title"
                        label="Título"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidTitle & introValidators.title
                                ? 'Título invalido'
                                : ''
                        "
                        @blur="blurTitle"
                        @click-icon="openProduction('')"
                    >
                        <template v-if="itemComponent.active" #icon>
                            🚀
                        </template>
                    </custom-input>
                </div>
                <div class="w-full mt-2">
                    <custom-input
                        v-model="itemComponent.summary"
                        label="Descripción"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSummary & introValidators.summary
                                ? 'Descripción invalida. Debe tener mínimo 1 caracter máximo 255'
                                : ''
                        "
                        @blur="introValidators.summary = true"
                    />
                </div>
                <div class="w-full mt-2">
                    <custom-input
                        v-model="itemComponent.slug"
                        label="Slug"
                        :disabled="!enabledUpdate"
                        :error="
                            !isValidSlug & introValidators.slug
                                ? 'Slug de página inválido'
                                : ''
                        "
                        @blur="introValidators.slug = true"
                    />
                </div>

                <div class="w-full mx-auto px-3 py-5">
                    <label for="content" class="font-bold"> Contenido </label>
                    <markdown-editor
                        v-model="itemComponent.content"
                        class="mt-2"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 mt-6 px-2">
                    <keywords-manage
                        v-model="itemComponent.keywords"
                        label="Palabras clave"
                        :enabled="enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-6 px-2">
                    <blog-types-selector
                        v-model="itemComponent.type"
                        :enabled="enabledUpdate"
                    />
                </div>
                <div class="w-full mt-2">
                    <label for="content" class="font-bold"> Imagen </label>
                    <image-manage
                        v-model="itemComponent.image"
                        :enabled="enabledUpdate"
                        message="Te recomendamos usar imágenes tipo custom"
                        class="mt-2"
                    />
                </div>

                <div
                    v-if="enabledUpdate"
                    class="w-full flex justify-center mt-5"
                >
                    <app-button
                        v-if="!slug"
                        size="large"
                        @click="createBlogComponent"
                    >
                        Crear página
                    </app-button>
                    <app-button
                        v-else
                        size="large"
                        @click="updateBlogComponent"
                    >
                        Actualizar página
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'products'" id="productsTab">
            <div class="flex flex-wrap">
                <div class="flex flex-col">
                    <h3 class="text-lg">Productos asociados</h3>
                    <span class="mt-3">
                        Una página personalizada es una herramienta valiosa para
                        brindar información, consejos y sugerencias sobre el uso
                        de los productos. Al asociar productos con una página,
                        se proporcionan enlaces directos para que los clientes
                        puedan realizar compras de manera sencilla y eficiente.
                    </span>
                </div>
            </div>
            <div v-if="enabledUpdate" class="mt-4">
                <h3 class="text-lg">
                    Busca los productos que quieres asociar a la página
                </h3>
                <references-search-scanner
                    :default-filters="{ active: true }"
                    :enabled-scanner="enabledScanner"
                    any
                    @input="validatePushReferences"
                />
            </div>
            <references-cards
                v-model="itemComponent.references"
                class="mt-2"
                :enabled="enabledUpdate"
                empty-message="Nuestro sistema NO encuentra productos asociados 0️⃣."
            />
            <div v-if="enabledUpdate" class="flex justify-center">
                <app-button
                    :loading="isLoadingProducts"
                    class="mt-4"
                    @click="updateBlogProductsComponent"
                >
                    Actualizar productos
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'files'" id="filesTab">
            <div id="line-container">
                <div id="line-title" class="flex flex-wrap">
                    <div class="flex flex-col">
                        <h2 class="text-theme-primary text-2xl">
                            Archivos adjuntos para la página
                        </h2>
                        <span>
                            Los archivos adjuntos, como guías de uso de
                            productos, código fuente y otros materiales
                            relevantes, pueden ser agregados a la página para
                            complementar y fortalecer el contenido que se está
                            compartiendo.
                        </span>
                    </div>
                </div>

                <files-manage
                    :id="itemComponent.slug"
                    v-model="itemComponent.files"
                    :enabled="enabledUpdate"
                    class="mt-3"
                />
                <div v-if="enabledUpdate" class="flex justify-center">
                    <app-button
                        :loading="isLoadingFiles"
                        class="mt-4"
                        @click="updateBlogFilesComponent"
                    >
                        Actualizar archivos
                    </app-button>
                </div>
            </div>
        </div>

        <div v-if="activeTab === 'advance'" id="advanceTab">
            <div id="title-routes" class="flex flex-wrap">
                <div class="flex flex-col">
                    <h2 class="text-theme-primary text-2xl">Avanzado</h2>
                    <span>
                        En esta sección podrás eliminar la página, ten mucho
                        cuidado.
                    </span>
                </div>
            </div>
            <div class="flex justify-around mt-5">
                <app-button
                    v-if="$ability.hasPermission('blog:Delete')"
                    danger
                    @click="deleteItemComponent"
                >
                    Eliminación
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { slugRegex, summaryRegex, titleRegex } from "@/common/lib/regex";
import { slugify } from "@/common/lib/transformers";
import { stripUrlProtocol } from "@/common/lib/url";

import BlogTypesSelector from "@/components/blogs/BlogTypesSelector.vue";
import ReferencesCards from "@/components/references/ReferencesCards.vue";
import ReferencesSearchScanner from "@/components/references/ReferencesSearchScanner.vue";
import ActiveSelector from "@/components/utils/ActiveSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import FilesManage from "@/components/utils/FilesManage.vue";
import ImageManage from "@/components/utils/ImageManage.vue";
import KeywordsManage from "@/components/utils/KeywordsManage.vue";
import MarkdownEditor from "@/components/utils/MarkdownEditor.vue";

export default {
    name: "CreateUpdateBlog",
    components: {
        ImageManage,
        FilesManage,
        MarkdownEditor,
        ActiveSelector,
        ReferencesCards,
        KeywordsManage,
        BlogTypesSelector,
        ReferencesSearchScanner,
        CustomInput
    },
    mixins: [tabQuerySyncMixin, planLimitError],
    data: () => ({
        itemComponent: {
            active: false,
            slug: "",
            title: "",
            content: "",
            summary: "",
            keywords: [],
            weight: 120,
            type: "tutorial",
            image: { id: 0 },
            files: [],
            references: []
        },
        introValidators: { slug: false, title: false, summary: false },
        activeTab: "general",
        $tabsWhitelist: ["general", "products", "files", "advance"],
        isLoading: false,
        isCreation: true,
        enabledUpdate: false,
        isLoadingFiles: false,
        isLoadingProducts: false,
        slug: "",
        title: "",
        enabledScanner: true
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidTitle() {
            return titleRegex.test(this.itemComponent.title);
        },
        isValidSummary() {
            return summaryRegex.test(this.itemComponent.summary);
        },
        publicBlogUrl() {
            if (!this.itemComponent?.slug || !this.$publicUrl) {
                return "";
            }
            return `${this.$publicUrl}/blogs/${this.itemComponent.slug}`;
        },
        publicBlogLabel() {
            return stripUrlProtocol(this.publicBlogUrl);
        },
        isValidSlug() {
            return slugRegex.test(this.itemComponent.slug);
        }
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        this.isCreation = true;
        if ("slug" in this.$route.params) {
            this.isCreation = false;
            if (!(await verifyPagePermission("blog:Get", "/app/blogs"))) return;
            const slug = this.$route.params.slug;
            this.slug = slug;
            await this.getBaseItem(slug);
        } else if ("slugClone" in this.$route.params) {
            if (!(await verifyPagePermission("blog:Create", "/app/blogs")))
                return;
            this.enabledUpdate = true;
            const slugTmp = this.$route.params.slugClone;
            this.slug = `${slugTmp}-clone`;
            await this.getBaseItem(slugTmp);
            this.itemComponent = {
                ...this.itemComponent,
                name: `${this.itemComponent.title} CLONE`,
                slug: `${this.itemComponent.slug}-clone`,
                active: false
            };
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Estás clonando una página",
                icon: "info",
                showCloseButton: true
            });
        } else {
            await verifyPagePermission("blog:Create", "/app/blogs");
            this.enabledUpdate = true;
        }
    },
    methods: {
        ...mapActions("blogs", [
            "getBlogBySlug",
            "updateBlog",
            "deleteBlog",
            "publishBlog",
            "createBlog",
            "updateBlogsFiles",
            "updateBlogsProducts"
        ]),
        async getBaseItem(slug) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const blog = await this.getBlogBySlug({ slug });
                const { products, ...otherProperties } = blog;
                this.itemComponent = {
                    ...otherProperties,
                    references: products.map((item) => ({
                        ...item.defaultReference,
                        product: {
                            id: item.id,
                            name: item.name,
                            slug: item.slug,
                            sku: item.sku,
                            brand: item.brand
                        }
                    }))
                };
                this.title = this.itemComponent.title;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$loading.hide();
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app/blogs");
                }
            }
        },
        async updateBlogComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.updateBlog(this.getDataSend());
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createBlogComponent() {
            if (!this.validateData()) {
                return;
            }
            this.isLoading = true;
            this.$loading.show();
            try {
                const data = await this.createBlog(this.getDataSend());
                this.$loading.hide();
                this.itemComponent = { ...data, ...this.itemComponent };
                this.$sound.play();
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Slug: ${data.slug}`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#d33ddd",
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a páginas personalizadas",
                    cancelButtonText: "Nueva página personalizada"
                });
                if (dialogResult.isConfirmed) {
                    this.slug = data.slug;
                    this.isCreation = false;
                    this.itemComponent = { ...data, ...this.itemComponent };
                    this.$router.push(`/app/blogs/detail/${this.slug}`);
                }
                if (dialogResult.isDenied) {
                    this.$router.push("/app/blogs/");
                }
                if (dialogResult.isDismissed) {
                    this.introValidators = {
                        slug: false,
                        title: false,
                        summary: false
                    };
                    this.itemComponent = {
                        title: "",
                        content: "",
                        slug: "",
                        summary: "",
                        keywords: [],
                        files: [],
                        products: [],
                        image: { id: null }
                    };
                }
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                if (this.openPlanUpgradeForLimit(error)) return;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteItemComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "¿Seguro deseas eliminar la página?",
                text: "Recuerda: se va a eliminar tu página y puedes perder tiempo 🗑️",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteBlog({
                        slug: this.itemComponent.slug
                    });
                    this.$toast.success({
                        title: `Página # ${this.itemComponent.id} eliminada`
                    });
                    this.$router.push("/app/blogs");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async setSlug() {
            if (
                this.enabledUpdate &&
                this.isValidTitle &&
                !this.itemComponent.active
            ) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Quieres cambiar el slug ?",
                    icon: "info",
                    showCancelButton: true,
                    confirmButtonColor: "#02913f",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Si",
                    cancelButtonText: "No"
                });
                if (dialogResult.isConfirmed) {
                    this.itemComponent.slug = slugify(this.itemComponent.title);
                }
            }
        },
        async updateBlogFilesComponent() {
            this.isLoadingFiles = true;
            try {
                await this.updateBlogsFiles({
                    blogSlug: this.itemComponent.slug,
                    filesIds: this.itemComponent.files.map((item) => ({
                        id: item.slug
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingFiles = false;
            } catch (error) {
                this.isLoadingFiles = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async updateBlogProductsComponent() {
            this.isLoadingProducts = true;
            try {
                await this.updateBlogsProducts({
                    blogSlug: this.itemComponent.slug,
                    productsIds: this.itemComponent.references.map((item) => ({
                        id: item.product.id
                    }))
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoadingProducts = false;
            } catch (error) {
                this.isLoadingProducts = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        blurTitle() {
            this.introValidators.title = true;
            this.setSlug();
        },
        validateData() {
            this.introValidators = {
                slug: true,
                title: true,
                summary: true
            };

            const toComplete = [];
            if (!this.isValidSlug) {
                toComplete.push("Slug");
            }

            if (!this.isValidTitle) {
                toComplete.push("Titulo");
            }

            if (!this.isValidSummary) {
                toComplete.push("Descripción");
            }

            if (!this.itemComponent.image.id) {
                toComplete.push("Imagen");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Página inválida",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        validatePushReferences(reference) {
            const index = this.itemComponent.references.findIndex(
                (item) => item.product.id === reference.product.id
            );
            if (index === -1) {
                this.itemComponent.references.unshift(reference);
                return;
            }
            this.$swal.fire({
                background: this.backgroundColor,
                title: "Producto ya incluido",
                text: `El producto ${reference.product.name} ya esta en la lista de productos asociados a la página`,
                icon: "info"
            });
        },
        getDataSend() {
            return {
                ...this.itemComponent,
                imageId: this.itemComponent.image.id
            };
        },
        async publishComponent() {
            this.isLoading = true;
            this.$loading.show();
            try {
                await this.publishBlog(this.itemComponent);
                this.$sound.play();
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.itemComponent.active = !this.itemComponent.active;
                this.$sound.play("error");
                this.$loading.hide();
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onFocus() {
            this.enabledScanner = false;
        },
        onBlur() {
            this.enabledScanner = true;
        },
        openProduction(complement) {
            window
                .open(
                    `${this.$publicUrl}/blogs/${this.itemComponent.slug}${complement}`,
                    "_blank"
                )
                .focus();
        }
    }
};
</script>
